import { makeApi } from "@zodios/core";
import { z } from "zod";

import { designerNodeDataSchema, nodeDesignsEntitySchema } from "@skilitics/nautilus-core-designer-data";

import { moduleSchema } from "./interactModules.ts";

const getNodeResponseBodySchema = nodeDesignsEntitySchema
  .pick({
    revision: true,
    appVersion: true,
    languages: true,
  })
  .extend({
    revision: z.number(),
    node: designerNodeDataSchema,
    primaryContentLanguage: z.string().optional(),
    metadata: moduleSchema.optional(),
  });

const moduleNodeSchema = z
  .object({ typeId: z.literal("module") })
  .passthrough()
  .pipe(designerNodeDataSchema);

const postNodeBaseSchema = getNodeResponseBodySchema
  .pick({
    node: true,
    appVersion: true,
    languages: true,
    primaryContentLanguage: true,
    metadata: true,
  })
  .extend({
    node: moduleNodeSchema,
    primaryContentLanguage: z.string().optional(),
    metadata: moduleSchema.optional(),
  });

export const postNodeRequestBodySchema = postNodeBaseSchema
  .refine((data) => data.languages.length > 0, {
    message: "At least one content language is required",
    path: ["languages"],
  })
  .refine(
    (data) => !data.primaryContentLanguage || data.languages.some((l) => l.code === data.primaryContentLanguage),
    {
      message: "primaryContentLanguage must be listed in languages",
      path: ["primaryContentLanguage"],
    },
  );

export type PostNodeRequestBody = z.infer<typeof postNodeRequestBodySchema>;

export const nodesApi = makeApi([
  {
    method: "get",
    path: "/content/:hostId/nodes",
    parameters: [
      {
        type: "Path",
        name: "hostId",
        schema: z.string(),
      },
      {
        type: "Query",
        name: "nodeId",
        schema: z.number().optional(),
      },
    ],
    alias: "getNode",
    description: "Get Node",
    response: getNodeResponseBodySchema,
    errors: [
      {
        status: 404,
        schema: z.string(),
      },
      {
        status: 412,
        schema: z.string(),
      },
    ],
  },
  {
    method: "post",
    path: "/content/nodes",
    parameters: [
      {
        type: "Body",
        name: "payload",
        schema: postNodeRequestBodySchema,
      },
    ],
    alias: "postNode",
    description: "Create module from node data",
    response: z.object({ hostId: z.string() }),
    status: 201,
    errors: [
      {
        status: 404,
        schema: z.string(),
      },
      {
        status: 418,
        schema: z.string(),
      },
    ],
  },
]);
